import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import './AuthPage.css';

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, user } = useAuth();
  const [mode, setMode] = useState(searchParams.get('mode') === 'signup' ? 'signup' : 'signin');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState('');

  const [signinForm, setSigninForm] = useState({ identifier: '', password: '' });
  const [signupForm, setSignupForm] = useState({ name: '', username: '', email: '', phone: '', password: '', otp: '' });
  // Forgot-password flow state (separate from the sign-up OTP state).
  const [forgotForm, setForgotForm] = useState({ email: '', otp: '', password: '' });
  const [forgotOtpSent, setForgotOtpSent] = useState(false);

  useEffect(() => { if (user) navigate('/'); }, [user, navigate]);

  const switchMode = (next) => {
    setMode(next);
    // Reset transient OTP/reset state so stale values don't leak between tabs.
    setOtp(''); setOtpSent(false); setOtpVerified(false);
    setForgotForm({ email: '', otp: '', password: '' }); setForgotOtpSent(false);
  };

  const handleForgotSendOtp = async () => {
    if (!forgotForm.email) { toast.error('Enter your registered email'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/forgot-password', { email: forgotForm.email });
      setForgotOtpSent(true);
      toast.success('If that email is registered, an OTP has been sent.');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to send OTP'); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const { email, otp: code, password } = forgotForm;
    if (!email || !code || !password) { toast.error('Please fill all fields'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { email, otp: code, password });
      toast.success('Password updated! Please sign in.');
      switchMode('signin');
      setSigninForm({ identifier: email, password: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Could not reset password'); }
    finally { setLoading(false); }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!signinForm.identifier || !signinForm.password) { toast.error('Please fill all fields'); return; }
    setLoading(true);
    try {
      await login(signinForm.identifier, signinForm.password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  const handleSendOtp = async () => {
    if (!signupForm.email) { toast.error('Enter your email first'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/send-otp', { email: signupForm.email });
      setOtpSent(true);
      toast.success('OTP sent to your email!');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to send OTP'); }
    finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otp) { toast.error('Enter the OTP'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/verify-otp', { email: signupForm.email, otp });
      setOtpVerified(true);
      toast.success('Email verified!');
    } catch { toast.error('Invalid OTP'); }
    finally { setLoading(false); }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    const { name, username, email, phone, password } = signupForm;
    if (!name || !username || !email || !phone || !password) { toast.error('Please fill all fields'); return; }
    if (!otpVerified) { toast.error('Please verify your email first'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register({ name, username, email, phone, password });
      toast.success('Account created successfully!');
      navigate('/');
    } catch (err) { toast.error(err.response?.data?.message || 'Registration failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft size={18}/></button>
          <div className="auth-logo" onClick={() => navigate('/')}>
            <span className="logo-main">Abhiras</span>
            <span className="logo-sub">Jewellery</span>
          </div>
        </div>

        <div className="auth-tabs">
          <button className={mode === 'signin' ? 'active' : ''} onClick={() => switchMode('signin')}>Sign In</button>
          <button className={mode === 'signup' ? 'active' : ''} onClick={() => switchMode('signup')}>Sign Up</button>
        </div>

        {mode === 'signin' && (
          <form className="auth-form" onSubmit={handleSignIn}>
            <div className="form-group">
              <label>Username / Email / Phone Number</label>
              <input type="text" placeholder="Enter username, email or phone" value={signinForm.identifier}
                onChange={e => setSigninForm(f => ({...f, identifier: e.target.value}))} />
            </div>
            <div className="form-group">
              <label>Password</label>
              <div className="pwd-wrap">
                <input type={showPwd ? 'text' : 'password'} placeholder="Enter password"
                  value={signinForm.password} onChange={e => setSigninForm(f => ({...f, password: e.target.value}))} />
                <button type="button" className="eye-btn" onClick={() => setShowPwd(s => !s)}>
                  {showPwd ? <FiEyeOff size={16}/> : <FiEye size={16}/>}
                </button>
              </div>
            </div>
            <button type="submit" className="btn-primary auth-submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
            <p className="auth-forgot-link">
              <button type="button" onClick={() => switchMode('forgot')}>Forgot password?</button>
            </p>
            <p className="auth-switch">Don't have an account? <button type="button" onClick={() => switchMode('signup')}>Sign Up</button></p>
          </form>
        )}

        {mode === 'forgot' && (
          <form className="auth-form" onSubmit={handleResetPassword}>
            <div className="form-group">
              <label>Registered Email</label>
              <div className="otp-row">
                <input type="email" placeholder="your@email.com" value={forgotForm.email}
                  onChange={e => setForgotForm(f => ({...f, email: e.target.value}))} disabled={forgotOtpSent} />
                {!forgotOtpSent && (
                  <button type="button" className="otp-btn" onClick={handleForgotSendOtp} disabled={loading}>
                    Get OTP
                  </button>
                )}
                {forgotOtpSent && (
                  <button type="button" className="otp-btn" onClick={handleForgotSendOtp} disabled={loading}>
                    Resend
                  </button>
                )}
              </div>
            </div>
            {forgotOtpSent && (
              <>
                <div className="form-group">
                  <label>Enter OTP</label>
                  <input type="text" placeholder="6-digit OTP" value={forgotForm.otp} maxLength={6}
                    onChange={e => setForgotForm(f => ({...f, otp: e.target.value.replace(/\D/,'')}))} />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <div className="pwd-wrap">
                    <input type={showPwd ? 'text' : 'password'} placeholder="Min. 6 characters"
                      value={forgotForm.password} onChange={e => setForgotForm(f => ({...f, password: e.target.value}))} />
                    <button type="button" className="eye-btn" onClick={() => setShowPwd(s => !s)}>
                      {showPwd ? <FiEyeOff size={16}/> : <FiEye size={16}/>}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn-primary auth-submit" disabled={loading}>
                  {loading ? 'Updating…' : 'Reset Password'}
                </button>
              </>
            )}
            <p className="auth-switch">Remembered it? <button type="button" onClick={() => switchMode('signin')}>Sign In</button></p>
          </form>
        )}

        {mode === 'signup' && (
          <form className="auth-form" onSubmit={handleSignUp}>
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" placeholder="Your full name" value={signupForm.name}
                onChange={e => setSignupForm(f => ({...f, name: e.target.value}))} />
            </div>
            <div className="form-group">
              <label>Username</label>
              <input type="text" placeholder="Choose a username" value={signupForm.username}
                onChange={e => setSignupForm(f => ({...f, username: e.target.value.toLowerCase().replace(/\s/,'')}))} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <div className="otp-row">
                <input type="email" placeholder="your@email.com" value={signupForm.email}
                  onChange={e => setSignupForm(f => ({...f, email: e.target.value}))} disabled={otpVerified} />
                {!otpVerified && (
                  <button type="button" className="otp-btn" onClick={handleSendOtp} disabled={loading || otpSent}>
                    {otpSent ? 'Resend' : 'Get OTP'}
                  </button>
                )}
                {otpVerified && <span className="verified-badge">✓ Verified</span>}
              </div>
            </div>
            {otpSent && !otpVerified && (
              <div className="form-group">
                <label>Enter OTP</label>
                <div className="otp-row">
                  <input type="text" placeholder="6-digit OTP" value={otp} maxLength={6}
                    onChange={e => setOtp(e.target.value.replace(/\D/,''))} />
                  <button type="button" className="otp-btn" onClick={handleVerifyOtp} disabled={loading}>Verify</button>
                </div>
              </div>
            )}
            <div className="form-group">
              <label>Phone Number</label>
              <input type="tel" placeholder="+91 98765 43210" value={signupForm.phone}
                onChange={e => setSignupForm(f => ({...f, phone: e.target.value}))} />
            </div>
            <div className="form-group">
              <label>Password</label>
              <div className="pwd-wrap">
                <input type={showPwd ? 'text' : 'password'} placeholder="Min. 6 characters"
                  value={signupForm.password} onChange={e => setSignupForm(f => ({...f, password: e.target.value}))} />
                <button type="button" className="eye-btn" onClick={() => setShowPwd(s => !s)}>
                  {showPwd ? <FiEyeOff size={16}/> : <FiEye size={16}/>}
                </button>
              </div>
            </div>
            <button type="submit" className="btn-primary auth-submit" disabled={loading}>
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
            <p className="auth-switch">Already have an account? <button type="button" onClick={() => setMode('signin')}>Sign In</button></p>
          </form>
        )}
      </div>
    </div>
  );
}
